import { ThemeEvents } from '@theme/events';

/**
 * apc: opens the size drawer from an add-to-cart button that is gated on variant selection.
 *
 * Volero requires a conscious size pick before add-to-cart (see `function.requires-selection`).
 * Rather than rendering the button disabled, the ATC stays a normal submit button and this
 * listener intercepts the click while the gate is still closed, opening the size drawer instead.
 *
 * The gate state is read from the picker's live `data-requires-selection` attribute, which
 * `variant-picker.js` removes as soon as the shopper picks the gated option. It is deliberately
 * NOT baked into the server-rendered markup: the section re-fetch after a pick uses
 * `?option_values=`, not `?variant=`, so `product.selected_variant` stays blank and the server
 * keeps reporting "still gated" even though the shopper has chosen. Reading the attribute at
 * click time is what lets add-to-cart work on the second click.
 *
 * The drawer lives in `blocks/apc-size-selector.liquid`, whose dialog id derives from a block id
 * the add-to-cart block can't know at render time, so it's discovered in the DOM, not wired by id.
 *
 * DELIBERATELY a module-scope singleton, not a Component: this is cross-section glue (the sticky
 * ATC is relocated to <body>; the drawer lives in another block), and the capture-phase click
 * listener must survive the Section Rendering API re-creating any would-be host mid-interaction.
 * The module is loaded from two blocks and de-dupes to one evaluation by ES module semantics.
 * Do not convert.
 */

/**
 * Finds the gated variant picker in scope for this button, if any.
 * @param {HTMLElement} button - The clicked add-to-cart button.
 * @returns {Element | null} The picker that still requires a selection, or null when un-gated.
 */
function findGatedPicker(button) {
  // The sticky button is relocated to <body>, so it can't rely on `closest` for its section.
  const sectionSelector = button.getAttribute('data-selection-scope');
  const scope = (sectionSelector && document.querySelector(sectionSelector)) || document;

  return scope.querySelector('variant-picker[data-requires-selection]');
}

document.addEventListener(
  'click',
  (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    const button = target?.closest('[data-selection-scope]');

    if (!(button instanceof HTMLElement)) return;

    const picker = findGatedPicker(button);

    // Gate already cleared — let the click through so the form submits and adds to cart.
    if (!picker) return;

    // Still gated: swallow the click so the form can't submit, and open the drawer instead.
    event.preventDefault();
    event.stopPropagation();

    const dialog = picker.querySelector('dialog-component');

    // `apc-size-selector` renders its options in a drawer; the base variant picker renders them
    // inline, so fall back to scrolling the picker into view there.
    if (dialog && 'showDialog' in dialog && typeof dialog.showDialog === 'function') {
      dialog.showDialog();
      return;
    }

    picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
  },
  // Capture phase: must beat both the delegated `on:click` handler and the form submit.
  true,
);

/**
 * apc: after a size is picked in the drawer, close the drawer and bring the buy box back into view.
 *
 * Base `variant-picker.js` already auto-closes via `this.closest('dialog-component')`, but in
 * `apc-size-selector` the dialog is a *child* of the picker, not an ancestor, so `closest` finds
 * nothing and the drawer stays open. Handled here rather than by patching base, so base-sync
 * can't overwrite it.
 */
document.addEventListener(ThemeEvents.variantSelected, async (event) => {
  const picker = /** @type {HTMLElement | null} */ (event.target)?.closest?.('variant-picker');
  if (!picker) return;

  // apc: pickers that carry their own footer (bottom add-to-cart / close buttons) stay open after a
  // pick so the shopper can act from within the drawer — the footer buttons drive the close now.
  if (picker.hasAttribute('data-keep-open-on-select')) {
    // Enable the footer add-to-cart the moment a size is picked, ahead of the section re-fetch that
    // would otherwise be the only thing to clear the server-rendered `disabled`.
    const footerAtc = /** @type {HTMLButtonElement | null} */ (picker.querySelector('[data-size-selector-atc]'));
    if (footerAtc) footerAtc.disabled = false;
    return;
  }

  const dialog = /** @type {any} */ (picker.querySelector('dialog-component'));
  if (!dialog?.refs?.dialog?.open || typeof dialog.closeDialog !== 'function') return;

  // `closeDialog` awaits the close animation and then restores the scroll position it captured on
  // open (the drawer uses scroll-lock). Anchoring before that resolves would be stomped by the
  // restore, so wait for it to finish before scrolling.
  await dialog.closeDialog();

  // Only pull the page back if the buy box isn't already on screen — a shopper who opened the
  // drawer from the in-view buy box shouldn't get a surprise scroll.
  const buyBox = picker.closest('.shopify-section')?.querySelector('product-form-component');
  if (!buyBox) return;

  const { top, bottom } = buyBox.getBoundingClientRect();
  const isInView = top < window.innerHeight && bottom > 0;
  if (isInView) return;

  buyBox.scrollIntoView({ behavior: 'smooth', block: 'center' });
});

/**
 * apc: the drawer's bottom "Add to cart" button delegates to the buy box's real add-to-cart button
 * rather than duplicating the form-submit + cart-open flow. The size-selector block can't know the
 * buy-buttons block id (its form id), so the real button is discovered in the DOM, section-scoped.
 *
 * The button is natively `disabled` until a size is picked, so by the time a click can land here the
 * gate is already cleared and `realAtc.click()` submits. The attribute guard below is a safety net.
 */
document.addEventListener('click', async (event) => {
  const atc = /** @type {HTMLElement | null} */ (event.target)?.closest?.('[data-size-selector-atc]');
  if (!atc) return;

  const picker = atc.closest('variant-picker');
  if (!picker || picker.hasAttribute('data-requires-selection')) return;

  const dialog = /** @type {any} */ (picker.querySelector('dialog-component'));
  const realAtc = /** @type {HTMLElement | null} */ (
    picker.closest('.shopify-section')?.querySelector('product-form-component button[name="add"]') ?? null
  );

  // Close the drawer first so its scroll-restore finishes before the real ATC opens the cart drawer.
  if (dialog?.refs?.dialog?.open && typeof dialog.closeDialog === 'function') await dialog.closeDialog();
  realAtc?.click();
});
